import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FamilyProvider } from './contexts/FamilyContext';

import Registration from './pages/Registration';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Logout from './pages/Logout';
import HomePage from './components/homepage/HomePage';
import FamilyTree from './components/familytree/FamilyTree';
import FamilyTreeBalkan from './components/familytree/FamilyTreeBalkan';
import FamilyManager from './components/familytree/FamilyManager';
import FamilyHub from './components/familytree/FamilyHub';
import FamilyMemberProfile from './components/familytree/FamilyMemberProfile';
import FamilyMemberProfilePage from './pages/FamilyMemberProfilePage';
import InviteFamilyScreen from './components/familytree/InviteFamilyScreen';
import AddChildScreen from './components/familytree/AddChildScreen';
import LetterCreation from './components/timecapsule/LetterCreation';
import TimeCapsuleDashboard from './components/timecapsule/TimeCapsuleDashboard';
import { MilestoneRegistry } from './components/milestone';
import { MyLifeHub } from './components/mylife/MyLifeHub';
import { MyLifeHubV2 } from './components/mylife/MyLifeHubV2';
import { MilestoneWizard } from './components/mylife/MilestoneWizard';
import LifeEventDashboard from './components/lifeevent/Dashboard';
import Hub from './pages/Hub';
import { CreateEvent } from './components/lifeevent/CreateEvent';
import { CreateEventGeneric } from './components/lifeevent/CreateEventGeneric';
import Today from './components/Today';
import TellAStory from './components/TellAStory';
import QuickLog from './components/QuickLog';
import LogDetail from './components/LogDetail';
import StoriesHub from './components/stories/StoriesHub';
import StoryDetail from './components/StoryDetail';
import MemoryDetail from './components/stories/MemoryDetail';
import EditStory from './components/stories/EditStory';

// New Authentication Components
import WelcomeScreen from './components/auth/WelcomeScreen';
import RegistrationFlow from './components/auth/RegistrationFlow';
import FamilySetup from './components/auth/FamilySetup';
import InviteFamily from './components/auth/InviteFamily';
import SetupComplete from './components/auth/SetupComplete';
import JoinFamily from './components/auth/JoinFamily';
import AcceptInvitation from './components/auth/AcceptInvitation';
import AddMinorFlow from './components/familytree/AddMinorFlow';

// Connection Model Demo
import ConnectionsDemo from './pages/ConnectionsDemo';

// CreateStory
import CreateStory from './pages/CreateStory';

// Senior Dashboard
import SeniorDashboard from './pages/SeniorDashboard';

// Family Board
import FamilyBoard from './pages/FamilyBoard';
import FamilyFeed from './pages/FamilyFeed';
import StorySaved from './pages/StorySaved';

// Dashboard Feed (redesigned)
import DashboardFeed from './pages/DashboardFeed';

// My Life Chapters
import MyLifeChapters from './pages/MyLifeChapters';
import MyLifeRedesign from './pages/MyLifeRedesign';
import MyLifePrompt from './pages/MyLifePrompt';
import ChapterDetailPage from './pages/ChapterDetail';

// Wisdom
import WisdomLibrary from './pages/WisdomLibrary';
import PhotosPage from './pages/PhotosPage';
import WisdomSpeakerProfile from './pages/WisdomSpeakerProfile';
import ChildLifeStory from './pages/ChildLifeStory';
import ChildSchoolYearsPage from './pages/ChildSchoolYearsPage';
import ChapterExpandedView from './pages/ChapterExpandedView';
import AddChapterMilestone from './pages/AddChapterMilestone';
import AddChapterMemory from './pages/AddChapterMemory';
import MemoryDetailView from './pages/MemoryDetailView';
import MilestoneDetailView from './pages/MilestoneDetailView';
import MilestoneEditWizard from './pages/MilestoneEditWizard';

// Retrieval
import AskPage from './pages/AskPage';

// Chapters
import AddChapter from './pages/AddChapter';

// Legal Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Settings
import SettingsPage from './pages/SettingsPage';
import EditFamilyMemberPage from './pages/EditFamilyMemberPage';
import ChangePassword from './pages/ChangePassword';
import VerifyEmail from './pages/VerifyEmail';
import DeleteAccount from './pages/DeleteAccount';
import RestoreAccount from './pages/RestoreAccount';
import PublicDeleteAccount from './pages/PublicDeleteAccount';
import PersonLifePage from './pages/PersonLifePage';

// Stewardship
import StewardedLetters from './pages/StewardedLetters';

// Family Management
import ManageFamilies from './components/common/ManageFamilies';

// Video Activation
import SpeakerConfirmation from './components/videoactivation/SpeakerConfirmation';

// Route Protection
import ProtectedRoute from './components/common/ProtectedRoute';

import './styles/global.css';

function RedirectToLife() {
  const { memberId } = useParams();
  return <Navigate to={`/life/${memberId}`} replace />;
}

function App() {
  return (
    <FamilyProvider>
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={<HomePage />} />
          
          {/* Legacy Routes */}
          <Route path="/register" element={<Registration />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/logout" element={<Logout />} />

          {/* Public Legal Pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />

          {/* Email Verification (public) */}
          <Route path="/verify-email/:token" element={<VerifyEmail />} />

          {/* Account Deletion (public) */}
          <Route path="/delete-account" element={<PublicDeleteAccount />} />
          <Route path="/account/restore/:token" element={<RestoreAccount />} />

          {/* Invitation Acceptance Route (must be before other routes) */}
          <Route path="/join/invitation/:id/:token" element={<AcceptInvitation />} />

          {/* New Authentication Flow */}
          <Route path="/auth" element={<Navigate to="/auth/register" replace />} />
          <Route path="/auth/register" element={<RegistrationFlow />} />
          <Route path="/auth/family-setup" element={<FamilySetup />} />
          <Route path="/auth/invite-family" element={<InviteFamily />} />
          <Route path="/auth/setup-complete" element={<SetupComplete />} />
          <Route path="/auth/join" element={<JoinFamily />} />
          
          {/* Protected App Routes - Require Authentication */}
          <Route path="/family-tree" element={<ProtectedRoute><FamilyTree /></ProtectedRoute>} />
          <Route path="/family-tree-v2" element={
            <ProtectedRoute>
              <div style={{height: '100vh'}}>
                <FamilyTreeBalkan
                  onPersonClick={(personId) => window.location.href = `/life/${personId}`}
                  activeFamilyId={localStorage.getItem('familyId')}
                />
              </div>
            </ProtectedRoute>
          } />
          <Route path="/family-manager" element={<ProtectedRoute><FamilyManager /></ProtectedRoute>} />
          <Route path="/family/:familyId/members/:personId/edit" element={<ProtectedRoute><EditFamilyMemberPage /></ProtectedRoute>} />
          <Route path="/manage-families" element={<ProtectedRoute><ManageFamilies /></ProtectedRoute>} />
          <Route path="/family-hub" element={<ProtectedRoute><FamilyHub /></ProtectedRoute>} />
          <Route path="/family-hub/invite" element={<ProtectedRoute><InviteFamilyScreen /></ProtectedRoute>} />
          <Route path="/family-hub/add-child" element={<ProtectedRoute><AddChildScreen /></ProtectedRoute>} />
          <Route path="/family/add-child" element={<ProtectedRoute><AddMinorFlow /></ProtectedRoute>} />
          <Route path="/family/:familyId/add-child" element={<ProtectedRoute><AddMinorFlow /></ProtectedRoute>} />
          <Route path="/life/:personId" element={<ProtectedRoute><PersonLifePage /></ProtectedRoute>} />
          <Route path="/family-hub/member/:memberId" element={<RedirectToLife />} />
          <Route path="/letter-creation" element={<ProtectedRoute><LetterCreation /></ProtectedRoute>} />
          <Route path="/timecapsule" element={<ProtectedRoute><TimeCapsuleDashboard /></ProtectedRoute>} />
          <Route path="/stewarded-letters" element={<ProtectedRoute><StewardedLetters /></ProtectedRoute>} />
          <Route path="/milestone-registry" element={<ProtectedRoute><MilestoneRegistry /></ProtectedRoute>} />

          {/* My Life Hub with Tabs (Legacy) */}
          <Route path="/my-life-old" element={<ProtectedRoute><MyLifeHubV2 /></ProtectedRoute>} />
          <Route path="/milestones" element={<ProtectedRoute><MyLifeHub /></ProtectedRoute>} />
          <Route path="/memories" element={<ProtectedRoute><MyLifeHub /></ProtectedRoute>} />

          {/* My Life Chapters (Redesigned) */}
          <Route path="/my-life/child/:memberId" element={<ProtectedRoute><ChildLifeStory /></ProtectedRoute>} />
          <Route path="/my-life/child/:memberId/school-years" element={<ProtectedRoute><ChildSchoolYearsPage /></ProtectedRoute>} />
          <Route path="/my-life" element={<ProtectedRoute><MyLifePrompt /></ProtectedRoute>} />
          <Route path="/my-life-old" element={<ProtectedRoute><MyLifeRedesign /></ProtectedRoute>} />
          <Route path="/my-life-old" element={<ProtectedRoute><MyLifeChapters /></ProtectedRoute>} />
          <Route path="/my-life/chapters/:chapterId" element={<ProtectedRoute><ChapterDetailPage /></ProtectedRoute>} />
          <Route path="/my-life/chapters/:chapterId/old" element={<ProtectedRoute><ChapterExpandedView /></ProtectedRoute>} />
          <Route path="/my-life/chapters/:chapterId/add-milestone" element={<ProtectedRoute><AddChapterMilestone /></ProtectedRoute>} />
          <Route path="/my-life/chapters/:chapterId/add-memory" element={<ProtectedRoute><AddChapterMemory /></ProtectedRoute>} />
          <Route path="/my-life/saved/:storyId" element={<ProtectedRoute><StorySaved /></ProtectedRoute>} />
          <Route path="/my-life/add-chapter" element={<ProtectedRoute><AddChapter /></ProtectedRoute>} />
          <Route path="/my-life/add-milestone" element={<ProtectedRoute><AddChapterMilestone /></ProtectedRoute>} />
          <Route path="/my-life/add-memory" element={<ProtectedRoute><AddChapterMemory /></ProtectedRoute>} />
          <Route path="/my-life/memories/:id" element={<ProtectedRoute><MemoryDetailView /></ProtectedRoute>} />
          <Route path="/my-life/milestones/:id" element={<ProtectedRoute><MilestoneDetailView /></ProtectedRoute>} />
          <Route path="/my-life/milestones/:id/edit" element={<ProtectedRoute><MilestoneEditWizard /></ProtectedRoute>} />

          {/* Family Wisdom */}
          <Route path="/wisdom" element={<ProtectedRoute><WisdomLibrary /></ProtectedRoute>} />
          <Route path="/wisdom/speaker/:speakerId" element={<ProtectedRoute><WisdomSpeakerProfile /></ProtectedRoute>} />

          {/* Ask — Family Memory Search */}
          <Route path="/ask" element={<ProtectedRoute><AskPage /></ProtectedRoute>} />

          {/* My Life Hub V2 (New Design) */}
          <Route path="/my-life-v2" element={<ProtectedRoute><MyLifeHubV2 /></ProtectedRoute>} />

          {/* Milestone Wizards - One route per category */}
          <Route path="/my-life-v2/add-education-milestone" element={<ProtectedRoute><MilestoneWizard category="education" /></ProtectedRoute>} />
          <Route path="/my-life-v2/add-career-milestone" element={<ProtectedRoute><MilestoneWizard category="career" /></ProtectedRoute>} />
          <Route path="/my-life-v2/add-family-milestone" element={<ProtectedRoute><MilestoneWizard category="family" /></ProtectedRoute>} />
          <Route path="/my-life-v2/add-home-milestone" element={<ProtectedRoute><MilestoneWizard category="home" /></ProtectedRoute>} />
          <Route path="/my-life-v2/add-travel-milestone" element={<ProtectedRoute><MilestoneWizard category="travel" /></ProtectedRoute>} />
          <Route path="/my-life-v2/add-achievement-milestone" element={<ProtectedRoute><MilestoneWizard category="achievement" /></ProtectedRoute>} />

          {/* Video Activation - Speaker Confirmation */}
          <Route path="/identify-voices/:jobId" element={<ProtectedRoute><SpeakerConfirmation /></ProtectedRoute>} />
          <Route path="/identify-voices" element={<Navigate to="/board" replace />} />

          {/* Story Creation */}
          <Route path="/share/memory" element={<ProtectedRoute><TellAStory /></ProtectedRoute>} />
          <Route path="/tell-a-story" element={<ProtectedRoute><TellAStory /></ProtectedRoute>} />
          <Route path="/quick-log" element={<ProtectedRoute><QuickLog /></ProtectedRoute>} />
          <Route path="/logs/:id" element={<ProtectedRoute><LogDetail /></ProtectedRoute>} />
          <Route path="/logs/:id/edit" element={<ProtectedRoute><QuickLog /></ProtectedRoute>} />

          {/* Life Events Routes */}
          <Route path="/lifeevent/dashboard" element={<ProtectedRoute><LifeEventDashboard /></ProtectedRoute>} />
          <Route path="/lifeevent/create" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
          <Route path="/lifeevent/create-generic" element={<ProtectedRoute><CreateEventGeneric /></ProtectedRoute>} />
          <Route path="/today" element={<ProtectedRoute><Today /></ProtectedRoute>} />
          <Route path="/stories/create" element={<ProtectedRoute><CreateStory /></ProtectedRoute>} />
          <Route path="/stories/:id/edit-v2" element={<ProtectedRoute><CreateStory /></ProtectedRoute>} />
          <Route path="/stories" element={<Navigate to="/board" replace />} />
          <Route path="/stories/:id" element={<ProtectedRoute><MemoryDetail /></ProtectedRoute>} />
          <Route path="/stories/:id/old" element={<ProtectedRoute><StoryDetail /></ProtectedRoute>} />
          <Route path="/stories/:id/edit" element={<ProtectedRoute><EditStory /></ProtectedRoute>} />
          <Route path="/dashboard" element={<Navigate to="/board" replace />} />
          <Route path="/dashboard-old" element={<ProtectedRoute><Hub /></ProtectedRoute>} />

          {/* Additional Routes */}
          <Route path="/photos" element={<ProtectedRoute><PhotosPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/settings/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          <Route path="/settings/account/delete" element={<ProtectedRoute><DeleteAccount /></ProtectedRoute>} />
          <Route path="/profile" element={<Navigate to="/settings" replace />} />

          {/* Connection Model Demo */}
          <Route path="/connections-demo" element={<ProtectedRoute><ConnectionsDemo /></ProtectedRoute>} />

          {/* Senior Dashboard (4 States) */}
          <Route path="/senior-dashboard" element={<ProtectedRoute><SeniorDashboard /></ProtectedRoute>} />

          {/* Family Board - New Senior Dashboard */}
          <Route path="/board" element={<ProtectedRoute><FamilyFeed /></ProtectedRoute>} />
          <Route path="/board-old" element={<ProtectedRoute><FamilyBoard /></ProtectedRoute>} />

        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </Router>
    </FamilyProvider>
  );
}

export default App;
